// apps/web/src/components/forms/AISuggestedField.tsx
//
// Phase 10 Plan 12 Task 1 — annotation wrapper for AI-suggested form fields // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
// (Surface 3 per 10-UI-SPEC §<AISuggestedField> spec lines 604-633).
//
// Thin pass-through wrapper around any form field. When the field's RHF path
// is in the session's AI-suggested-paths set, it draws a subtle left-border
// accent (border-l-primary/40 — a de-emphasized 40%-alpha variant, NOT a CTA
// accent) and attaches a Tooltip that, on focus, explains the value's origin.
// When the path is NOT in the set, it returns the children inline with ZERO
// DOM and ZERO subscription overhead (10-UI-SPEC §"render-storm prevention"
// rule 1) — which is also what makes the Phase 8 RecipeFormPanel wrapping
// backward-compatible (no-op when nothing is AI-suggested).
//
// data-ai-suggested="true" is the testable marker (10-UI-SPEC line 635):
// vitest asserts presence on suggested paths and absence elsewhere.
//
// Two-weight typography: the TooltipContent body renders at text-sm (Body
// weight 400) — no font-medium anywhere (Phase 10 carry-forward, 10-UI-SPEC
// line 85).

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAISuggestedPaths } from "@/pages/author/useAISuggestedPaths";

export interface AISuggestedFieldProps {
  path: string; // RHF path; used to look up suggested-paths
  children: React.ReactNode;
}

export function AISuggestedField({ path, children }: AISuggestedFieldProps): React.ReactElement {
  const suggestedPaths = useAISuggestedPaths();
  const isAISuggested = suggestedPaths.has(path);

  if (!isAISuggested) {
    return <>{children}</>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="border-l-4 border-l-primary/40 pl-3" data-ai-suggested="true">
          {children}
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="text-sm">
        AI extracted — confirm or edit.
      </TooltipContent>
    </Tooltip>
  );
}
